import { io, Socket } from 'socket.io-client'

/**
 * A service manager for handling WebSocket connections using socket.io.
 * This class follows a singleton pattern to ensure a single instance of the socket connection.
 */
export class SocketServiceManager {
  /**
   * The single instance of the SocketServiceManager.
   * @type {SocketServiceManager}
   */
  static instance: SocketServiceManager

  /**
   * The socket.io instance.
   * @type {Socket | undefined}
   */
  socket: Socket | undefined

  /**
   * The WebSocket URL.
   * @type {string | undefined}
   */
  url: string | undefined

  /**
   * Registers the WebSocket service with the given URL.
   * @param {string} url - The WebSocket URL.
   * @throws {Error} If the URL is not provided.
   */
  public static registerService(url: string) {
    if (!url) throw new Error('url is required')
    const instance = SocketServiceManager.getInstance()
    instance.socket = io(url)
    instance.url = url
  }

  /**
   * Gets the single instance of the SocketServiceManager.
   * If the instance does not exist, it creates a new one.
   * @returns {SocketServiceManager} The single instance of the SocketServiceManager.
   */
  static getInstance(): SocketServiceManager {
    if (!SocketServiceManager.instance) {
      SocketServiceManager.instance = new SocketServiceManager()
    }
    return SocketServiceManager.instance
  }

  /**
   * Connects to a specific WebSocket connection and listens for messages.
   * @template TMessage
   * @param {string} connection - The connection string to listen to.
   * @param {(message: TMessage) => void} listener - The callback function to handle received messages.
   */
  static connectTo<TMessage = any>(connection: string, listener: (message: TMessage) => void) {
    const socket = SocketServiceManager.getInstance().socket
    socket?.off(connection)
    SocketServiceManager.getInstance().socket?.on?.(connection, listener)
  }

  /**
   * Disconnects from a specific WebSocket connection.
   * @param {string} connection - The connection string to disconnect from.
   */
  static disconnectTo(connection: string) {
    SocketServiceManager.getInstance().socket?.off?.(connection)
  }

  /**
   * Emits a message to a specific WebSocket connection.
   * @template TMessage
   * @param {string} connection - The connection string to emit the message to.
   * @param {TMessage} message - The message to emit.
   */
  static emit<TMessage = any>(connection: string, message: TMessage) {
    SocketServiceManager.getInstance().socket?.emit(connection, message)
  }
}
